import api from './axios'

// Client for /api/super-admin/news (CITS-Server/docs/news-api.md).

export type NewsCategory = 'release' | 'fix' | 'maintenance' | 'tip' | 'company' | 'security'
export type NewsAudience = 'app' | 'portal' | 'both'
export type NewsRole = 'employee' | 'manager' | 'admin'
export type NewsPlatform = 'ios' | 'android' | 'web'
export type NewsStatus = 'draft' | 'scheduled' | 'published'
export type LibraryStatus = NewsStatus | 'expired'

export type NewsBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[]; ordered?: boolean }
  | { type: 'callout'; tone: 'info' | 'warn'; text: string }
  | { type: 'image'; url: string; alt: string }
  | { type: 'button'; label: string; target: string }

export interface NewsStats {
  reached: number
  opened: number
  cta: number
}

/** Fields the composer edits. Times: see docs/news-api.md ("Times"). */
export interface NewsInput {
  title: string
  summary: string
  category: NewsCategory
  version: string | null
  blocks: NewsBlock[]
  cover_image_url: string | null
  audience: NewsAudience
  companies_mode: 'all' | 'some'
  companies: string[]
  roles: NewsRole[]
  platforms: NewsPlatform[]
  min_app_version: string | null
  /** fixed: ISO instant with Z; company: "YYYY-MM-DDTHH:mm" wall clock */
  starts_at: string | null
  expires_at: string | null
  tz_mode: 'fixed' | 'company'
  tz: string | null
  pinned: boolean
  priority: 'normal' | 'high'
  push: boolean
  email_admins: boolean
}

export interface NewsItem extends NewsInput {
  id: string
  status: NewsStatus
  library_status: LibraryStatus
  published_at: string | null
  legacy: boolean
  created_by: string | null
  updated_by: string | null
  created_at: string | null
  updated_at: string | null
  stats: NewsStats
  by_company: Array<NewsStats & { company_code: string; company_name: string | null }>
  sends: Array<{ company_code: string; channel: 'push' | 'email'; status: string; recipients: number; sent: number; failed: number; error: string | null; at: string | null }>
}

export type NewsListItem = Omit<NewsItem, 'blocks' | 'companies' | 'by_company' | 'sends'> & {
  targets_count: number
  where: string
}

export interface NewsListResponse {
  items: NewsListItem[]
  counts: Record<'all' | LibraryStatus, number>
  total: number
}

export interface RouteInfo { label: string; path: string }
export interface NewsMeta {
  appRoutes: Record<string, RouteInfo>
  portalRoutes: Record<string, RouteInfo>
  limits: Record<string, number>
}

export interface ReachEstimate {
  total: number
  companies: number
  byRole: Record<NewsRole, number>
  byPlatform: Record<NewsPlatform, number>
  byCompany: Array<{ company_code: string; company_name: string; total: number }>
  unavailable: string[]
}

export async function listNews(status: 'all' | LibraryStatus = 'all'): Promise<NewsListResponse> {
  return (await api.get('/super-admin/news', { params: { status } })).data
}

export async function getNews(id: string): Promise<NewsItem> {
  return (await api.get(`/super-admin/news/${id}`)).data
}

export async function getNewsMeta(): Promise<NewsMeta> {
  return (await api.get('/super-admin/news/meta')).data
}

export async function createNews(input: NewsInput): Promise<NewsItem> {
  return (await api.post('/super-admin/news', input)).data
}

export async function updateNews(id: string, input: Partial<NewsInput>): Promise<NewsItem> {
  return (await api.put(`/super-admin/news/${id}`, input)).data
}

export async function publishNews(id: string, now = false): Promise<NewsItem> {
  return (await api.post(`/super-admin/news/${id}/publish`, { now })).data
}

export async function unpublishNews(id: string): Promise<NewsItem> {
  return (await api.post(`/super-admin/news/${id}/unpublish`)).data
}

export async function deleteNews(id: string): Promise<void> {
  await api.delete(`/super-admin/news/${id}`)
}

export async function sendTestNews(id: string): Promise<{ sent: number; companies: string[] }> {
  return (await api.post(`/super-admin/news/${id}/test`)).data
}

export async function estimateReach(filters: Pick<NewsInput, 'companies_mode' | 'companies' | 'audience' | 'roles' | 'platforms' | 'min_app_version'>): Promise<ReachEstimate> {
  return (await api.post('/super-admin/news/reach', filters)).data
}

export async function uploadNewsImage(file: File): Promise<{ id: string; url: string; width: number; height: number }> {
  const form = new FormData()
  form.append('file', file)
  return (await api.post('/super-admin/news/media', form)).data
}

export async function aiWriteNews(params: {
  title: string
  notes: string
  category: NewsCategory
  audience: NewsAudience
  tone: 'professional' | 'friendly' | 'brief' | 'detailed'
  length: 'short' | 'medium' | 'long'
  fromTitle: boolean
}): Promise<{ summary: string; blocks: NewsBlock[]; source: 'ai' | 'template' }> {
  return (await api.post('/super-admin/news/ai/write', params)).data
}

/** Fields a pasted JSON document may set (docs/news-api.md section 10). */
export const IMPORT_FIELDS = [
  'title', 'summary', 'category', 'version', 'blocks', 'cover_image_url',
  'audience', 'platforms', 'roles', 'pinned', 'priority', 'push', 'email_admins',
] as const
export type NewsImportField = typeof IMPORT_FIELDS[number]
export type NewsImportItem = Partial<Pick<NewsInput, NewsImportField>>

export interface NewsImportError { field: string | null; block?: number; message: string }
export type NewsImportResult =
  | { ok: true; item: NewsImportItem; ignored: string[] }
  | { ok: false; error: string; errors: NewsImportError[]; ignored: string[] }

/** A release-news/*.json draft shipped with the server (Import › From this release). */
export interface ReleaseDraft {
  file: string
  /** sha256 of the file text: an edited draft is a new draft. */
  hash: string
  title: string | null
  summary: string | null
  category: string | null
  version: string | null
  audience: string | null
  updatedAt: string
  json: string
  error: string | null
  /** First time a server listed this exact content; null when not tracked. */
  firstSeenAt: string | null
  ageDays: number | null
  old: boolean
  released: { at: string; by: string | null; newsId: string | null } | null
  hiddenAt: string | null
  state: 'new' | 'released' | 'hidden' | 'old'
}

export interface ReleaseDraftList {
  /** false until migrations-2026-10-18-news-release-drafts runs: everything listed, nothing remembered. */
  tracking: boolean
  maxAgeDays: number
  drafts: ReleaseDraft[]
}

export interface ReleaseDraftRef { file: string; hash: string }

/** all=true adds released, hidden and old drafts (with their state). */
export async function listReleaseDrafts(all = false): Promise<ReleaseDraftList> {
  const data = (await api.get('/super-admin/news/release-drafts', { params: all ? { all: 1 } : undefined })).data
  return { tracking: !!data?.tracking, maxAgeDays: data?.maxAgeDays ?? 10, drafts: data?.drafts ?? [] }
}

export async function markReleaseDraftReleased(ref: ReleaseDraftRef & { newsId?: string | null }): Promise<void> {
  await api.post('/super-admin/news/release-drafts/release', ref)
}

export async function unmarkReleaseDraftReleased(ref: ReleaseDraftRef): Promise<void> {
  await api.post('/super-admin/news/release-drafts/unrelease', ref)
}

/** Checks pasted JSON with the server's save rules. A 400 is a result, not a throw. */
export async function validateNewsImport(json: string): Promise<NewsImportResult> {
  try {
    return (await api.post('/super-admin/news/validate', { json })).data
  } catch (err) {
    const data = (err as { response?: { status?: number; data?: NewsImportResult } })?.response
    if (data?.status === 400 && data.data && data.data.ok === false) return data.data
    throw err
  }
}

/** The import document for a composer draft: the inverse of an import. */
export function toImportDocument(input: NewsInput): NewsImportItem {
  const out: Record<string, unknown> = {}
  for (const k of IMPORT_FIELDS) out[k] = input[k]
  if (!input.title) delete out.title // an untitled draft: "" would not import back
  return out as NewsImportItem
}

export async function migrateLegacyNews(dryRun: boolean): Promise<{
  legacyRows: number
  groups: number
  alreadyMigrated: number
  toCreate: number
  readsFound: number
  readsCopied: number
  preview: Array<{ title: string; legacyRows: number; companies: 'all' | string[]; category: NewsCategory; status: NewsStatus; blocks: number; warnings: string[] }>
}> {
  return (await api.post('/super-admin/news/migrate-legacy', { dryRun })).data
}

export function apiError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string }
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback
}
